import type { Broadcast } from '@/models/broadcast';
import type { LiveProgram } from '@/models/channel';
import type { Input } from '@/models/input';
import { isInputOverallStatusLiveSessionActive } from '@/models/input';
import { FuelEntityType, FuelPortalService } from '@/services';
import { FuelToolsApiService } from '@/services/FuelToolsApi/fuelToolsApi.service';

export const PREVIEW_MAX_WAIT_MS = 180_000; // 180 seconds (3 minutes)
export const PREVIEW_MAX_WAIT_LABEL = '3 minutes';
export const PREVIEW_RETRY_INTERVAL_MS = 3000;

export const STATUS_POLL_CONNECTED_MS = 15_000;
export const STATUS_POLL_DISCONNECTED_MS = 30_000;

export type LiveProgramListQueryParams = {
  ouId: string | undefined;
  page: number;
  size: number;
  keyword: string;
  sortBy: string;
  orderBy: string;
  enabledOnly: boolean;
  showDeleted: boolean;
  searchByInputId?: string;
};

export const buildLiveProgramListQuery = (
  ouId: string | undefined,
  searchByInputId?: string
): LiveProgramListQueryParams => ({
  ouId,
  page: 0,
  size: 1000,
  keyword: '',
  sortBy: 'name',
  orderBy: 'asc',
  enabledOnly: true,
  showDeleted: false,
  ...(searchByInputId ? { searchByInputId } : {}),
});

export const getLivePrograms = async (
  ouId: string | undefined,
  searchByInputId?: string
): Promise<LiveProgram[]> => {
  if (!ouId) return [];
  const response = await FuelPortalService.getList(
    FuelEntityType.LiveProgram,
    buildLiveProgramListQuery(ouId, searchByInputId)
  );
  return response?.data ?? [];
};

export const getLiveProgramById = async (liveProgramId: string): Promise<LiveProgram | null> => {
  if (!liveProgramId) return null;
  try {
    return await FuelPortalService.getEntityById<LiveProgram>(FuelEntityType.LiveProgram, liveProgramId);
  } catch {
    return null;
  }
};

export type StreamConnectionState = 'unknown' | 'connected' | 'disconnected' | 'error';

export const normalizeConnection = (status?: string): StreamConnectionState => {
  const value = status?.toLowerCase().trim();
  if (value === 'connected' || value === 'recording') return 'connected';
  if (value === 'error') return 'error';
  if (value === 'disconnected') return 'disconnected';
  return 'unknown';
};

export type StreamStatusResult = {
  isManualActive: boolean;
  connectionState: StreamConnectionState;
  overallStatus?: string;
};

export const idsMatch = (a?: string | null, b?: string | null): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

export type InputStreamStatusFields = Pick<
  Input,
  'overallStatus' | 'sourceStatus' | 'liveProgramIds' | 'currentLiveProgramIds'
>;

export const fetchInputStreamStatusFields = async (inputId: string): Promise<InputStreamStatusFields> => {
  const response = (await FuelToolsApiService.getInputStreamStatus(inputId)) as Record<string, unknown> | null;
  if (!response) {
    return {};
  }

  return {
    overallStatus: (response.overallStatus ?? response.status) as string | undefined,
    sourceStatus: response.sourceStatus as string | undefined,
    liveProgramIds: response.liveProgramIds as string[] | undefined,
    currentLiveProgramIds: response.currentLiveProgramIds as string[] | undefined,
  };
};

export const mergeStreamStatusIntoInput = (input: Input, stream: InputStreamStatusFields): Input => {
  // Stream status API uses liveProgramIds for currently-recording programs.
  // Input.liveProgramIds are scheduled links — do not overwrite them from stream status.
  const recordingIds = stream.currentLiveProgramIds ?? stream.liveProgramIds;

  return {
    ...input,
    ...(stream.overallStatus != null ? { overallStatus: stream.overallStatus } : {}),
    ...(stream.sourceStatus != null ? { sourceStatus: stream.sourceStatus } : {}),
    ...(recordingIds != null ? { currentLiveProgramIds: recordingIds } : {}),
  };
};

export const fetchCurrentManualLiveStatus = async (
  inputId: string
): Promise<{ isManualActive: boolean; overallStatus?: string; sourceStatus?: string }> => {
  const stream = await fetchInputStreamStatusFields(inputId);
  if (!stream.overallStatus) {
    return { isManualActive: false };
  }

  return {
    isManualActive: isInputOverallStatusLiveSessionActive(stream.overallStatus),
    overallStatus: stream.overallStatus,
    sourceStatus: stream.sourceStatus,
  };
};

export const fetchStreamStatus = async (
  inputId: string,
  fallbackStatus?: string,
  fallbackSourceStatus?: string
): Promise<StreamStatusResult> => {
  const { isManualActive, overallStatus, sourceStatus } = await fetchCurrentManualLiveStatus(inputId);
  const resolvedOverall = overallStatus ?? fallbackStatus;
  const resolvedSource = sourceStatus ?? fallbackSourceStatus;

  if (isManualActive) {
    return {
      isManualActive: true,
      connectionState: normalizeConnection(resolvedSource) !== 'unknown'
        ? normalizeConnection(resolvedSource)
        : 'connected',
      overallStatus: resolvedOverall,
    };
  }

  const fromSource = normalizeConnection(resolvedSource);
  if (fromSource !== 'unknown') {
    return { isManualActive: false, connectionState: fromSource, overallStatus: resolvedOverall };
  }

  const connectionState = normalizeConnection(resolvedOverall);
  return {
    isManualActive: false,
    connectionState: connectionState === 'unknown' ? normalizeConnection(fallbackSourceStatus) : connectionState,
    overallStatus: resolvedOverall,
  };
};

export type ManualLiveActionType = 'Start' | 'End';

export type LivePreviewSession = {
  jobId: string;
  livePreviewUrl: string;
  inputId: string;
};

/** Mirrors InputList initiateLivePreview — starts preview before showing the player. */
export const initiateLivePreview = async (
  inputId: string,
  ouId: string
): Promise<LivePreviewSession | null> => {
  try {
    const response = await FuelToolsApiService.livePreviewStart({ inputId, ouId });
    if (!response?.livePreviewUrl) {
      return null;
    }
    return {
      jobId: response.jobId,
      livePreviewUrl: response.livePreviewUrl,
      inputId,
    };
  } catch {
    return null;
  }
};

export type ManualLivePayload = {
  liveProgramId: string;
  liveProgramName: string;
  inputDetails: Input;
  actionType: ManualLiveActionType;
};

export const runManualAction = async (payload: ManualLivePayload) => {
  if (payload.actionType === 'Start') {
    return FuelToolsApiService.manualLiveStart(payload);
  }
  return FuelToolsApiService.manualLiveStop(payload);
};

export const findBookingForLiveProgramId = <T extends Broadcast>(
  liveProgramId: string,
  bookings: T[],
  programs: LiveProgram[]
): T | null => {
  for (const booking of bookings) {
    const program = resolveLiveProgramForBroadcast(booking, programs);
    if (program?.id && idsMatch(program.id, liveProgramId)) {
      return booking;
    }
  }
  return null;
};

/** Maps a selected broadcast to a live program for manualLiveStart/Stop payloads. */
export const resolveLiveProgramForBroadcast = (
  broadcast: Broadcast,
  programs: LiveProgram[]
): LiveProgram | null => {
  const broadcastId = broadcast.sourceBroadcastId ?? broadcast.id;
  if (broadcastId && !String(broadcastId).startsWith('schedule:')) {
    const byId = programs.find((program) => program.id === broadcastId);
    if (byId) return byId;
  }

  const channelId = broadcast.destinations?.linearChannels?.[0];
  if (channelId) {
    const byChannel = programs.find((program) => program.id === channelId);
    if (byChannel) return byChannel;
  }

  const byName = programs.find((program) => program.name === broadcast.title);
  if (byName) return byName;

  return null;
};
